import * as React from "react"
import AuthorAvatar from "@/components/AuthorAvatar"
import {
  useTranslation,
  Trans,
  Link,
  useI18next,
} from "@zestia/gatsby-plugin-react-i18next"
import { formatDateString } from "@/utils/format-date"
import WistiaVideoEmbed from "@/components/WistiaVideoEmbed"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import SanityGatsbyImage from "@/components/SanityGatsbyImage"
import { gtmTracking } from "@/utils/gtm-tracking"

const Categories = ({ categories }) => {
  const { t } = useTranslation()

  return categories.map((category, index) => {
    let gtmCategory = `${category.charAt(0).toUpperCase()}${category.substring(
      1,
      category.length,
    )}`

    if (category === "crm") {
      gtmCategory = gtmCategory.toUpperCase()
    }

    return (
      <span className="blog-post-header__category" key={category}>
        <Link
          to={`/blog/category/${category}/`}
          onClick={() => gtmTracking("Link Click", category, "Blog Category")()}
          className="text-link text-link--bare blog-post-header__category-link"
        >
          {t(`blog.category.${category}`)}
        </Link>
        {index < categories.length - 1 && ", "}
      </span>
    )
  })
}

const BlogPostHeader = ({ post }) => {
  const { language } = useI18next()

  return (
    <header className="blog-post-header mt-10">
      {/* Title & Summary */}
      <div className="blog-post-header__info md:pr-16 lg:pr-32">
        <span className="blog-post-header__categories">
          <Categories
            categories={
              post.internal.type === "SanityBlogPost"
                ? post.categories
                : post.frontmatter.categories
            }
          />
        </span>

        <h1 className="blog-post-header__title mb-6 vs:mb-8 sm:mb-12 h2">
          {post.internal.type === "SanityBlogPost"
            ? post.title
            : post.frontmatter.title}
        </h1>

        {post.internal.type === "SanityBlogPost"
          ? post.summary && (
              <p className="blog-post-header__description">{post.summary}</p>
            )
          : post.frontmatter.summary && (
              <p className="blog-post-header__description">
                {post.frontmatter.summary}
              </p>
            )}

        <div className="blog-post-header__meta mt-4">
          <span className="blog-post-header__author">
            <AuthorAvatar
              author={
                post.internal.type === "SanityBlogPost"
                  ? post.author.name
                  : post.frontmatter.author
              }
            />

            {post.internal.type === "SanityBlogPost"
              ? post.author.name
              : post.frontmatter.author}
          </span>
          &nbsp;&middot;&nbsp;
          <span>
            {formatDateString({
              locale: language,
              dateString:
                post.internal.type === "SanityBlogPost"
                  ? post.publishedAt
                  : post.frontmatter.date,
              options: { month: "long", day: "numeric", year: "numeric" },
            })}
          </span>
        </div>
      </div>

      <div className="blog-post-header__media">
        {/* Image/Video */}
        {post.internal.type === "SanityBlogPost" ? (
          post.wistiaVideo?.id ? (
            <WistiaVideoEmbed videoId={post.wistiaVideo?.id} />
          ) : (
            post.coverImage && (
              <>
                <SanityGatsbyImage
                  image={post.coverImage}
                  alt={post.title}
                  className="blog-post__hero-image"
                  maxWidth={800}
                  quality={70}
                  style={{
                    width: "100%",
                    height: "100%",
                    aspectRatio: "16/9",
                  }}
                  objectFit="cover"
                />
              </>
            )
          )
        ) : post.frontmatter.social_video ? (
          <WistiaVideoEmbed videoId={post.frontmatter.social_video} />
        ) : (
          post.frontmatter.social_image && (
            <GatsbyImage
              className="blog-post__hero-image"
              alt={post.frontmatter.social_image.alt ?? post.frontmatter.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                aspectRatio: "16/9",
              }}
              quality={100}
              image={getImage(post.frontmatter.social_image.src)}
            />
          )
        )}
      </div>
    </header>
  )
}

export default BlogPostHeader
